<template>
  <!--
    AB#21984 — the dashboard search box, with suggestions while typing.

    The field and its magnifier button are the ones the toolbar always had
    (AB#21247): Enter or the button runs the search. New: after a short pause
    in typing a list opens underneath — matching orders (open one straight
    away), then the companies and customers named on them (search for that
    name). With the field empty and focused it offers the last searches.
    Arrow keys move, Enter chooses, Esc closes; Enter with nothing highlighted
    is the plain search, exactly as before.
  -->
  <div ref="root" class="dash-search-group dsb" data-test="dashboard-search-box">
    <label class="dash-search">
      <input
        id="dashboard-search"
        ref="input"
        type="search"
        role="combobox"
        autocomplete="off"
        aria-autocomplete="list"
        aria-controls="dashboard-search-list"
        :aria-expanded="open"
        :aria-activedescendant="active >= 0 ? `dsb-opt-${active}` : undefined"
        :value="modelValue"
        placeholder="Search orders, customers, references…"
        aria-label="Search orders"
        @input="onInput(($event.target as HTMLInputElement).value)"
        @focus="focused = true"
        @blur="focused = false"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.esc="close"
        @keydown.enter.prevent="onEnter"
      />
    </label>
    <button
      type="button"
      class="dash-search-btn"
      title="Search"
      aria-label="Search orders"
      @click="runSearch(modelValue)"
    >
      <MagnifyingGlassIcon class="size-5" />
    </button>

    <div
      v-if="open"
      id="dashboard-search-list"
      class="dsb-pop"
      role="listbox"
      aria-label="Search suggestions"
      data-test="dashboard-search-suggestions"
    >
      <template v-for="group in groups" :key="group.title">
        <div class="dsb-grp">
          <span>{{ group.title }}</span>
          <button
            v-if="group.kind === 'recent'"
            type="button"
            class="dsb-clear"
            data-test="dsb-clear-recent"
            @mousedown.prevent="forget"
          >
            Clear
          </button>
        </div>
        <button
          v-for="opt in group.options"
          :id="`dsb-opt-${opt.index}`"
          :key="opt.key"
          type="button"
          role="option"
          class="dsb-opt"
          :class="{ 'dsb-opt--on': opt.index === active }"
          :aria-selected="opt.index === active"
          :data-test="`dsb-opt-${opt.kind}`"
          @mousedown.prevent="choose(opt)"
          @mousemove="active = opt.index"
        >
          <template v-if="opt.kind === 'order'">
            <span class="dsb-no">#{{ opt.order!.order_no ?? 'pending' }}</span>
            <span class="dsb-main">
              {{ opt.order!.company_name }}
              <span v-if="opt.order!.customer_name" class="dsb-sub">
                · {{ opt.order!.customer_name }}
              </span>
            </span>
            <span v-if="opt.order!.status" class="dsb-status">{{
              statusText(opt.order!.status)
            }}</span>
          </template>
          <template v-else>
            <component :is="iconFor(opt.kind)" class="dsb-ic" aria-hidden="true" />
            <span class="dsb-main">{{ opt.text }}</span>
            <span class="dsb-hint">{{ opt.kind === 'recent' ? 'Search again' : 'Search' }}</span>
          </template>
        </button>
      </template>

      <button
        v-if="askOption"
        :id="`dsb-opt-${askOption.index}`"
        type="button"
        role="option"
        class="dsb-opt dsb-opt--ask"
        :class="{ 'dsb-opt--on': askOption.index === active }"
        :aria-selected="askOption.index === active"
        data-test="dsb-opt-ask"
        @mousedown.prevent="choose(askOption)"
        @mousemove="active = askOption.index"
      >
        <ElsaMark class="dsb-ic dsb-ic--elsa" aria-hidden="true" />
        <span class="dsb-main">Ask ELSA: “{{ typed }}”</span>
        <span class="dsb-hint">Turn it into filters</span>
      </button>

      <div v-if="!groups.length && loading" class="dsb-note" data-test="dsb-loading">
        Searching…
      </div>
      <div v-else-if="!groups.length && !askOption" class="dsb-note" data-test="dsb-none">
        No quick match. Press Enter to search all orders.
      </div>
      <div v-else-if="groups.length || askOption" class="dsb-foot">
        <kbd>↑</kbd><kbd>↓</kbd> to move · <kbd>Enter</kbd> to choose · <kbd>Esc</kbd> to close
      </div>
    </div>

    <!-- What ELSA made of the sentence. It stays until closed, so the person
         can see which filters are on and take them all off in one click. -->
    <div
      v-if="asking || asked || askError"
      class="dsb-pop dsb-ask"
      role="status"
      aria-live="polite"
      data-test="dsb-ask-panel"
    >
      <div class="dsb-ask-head">
        <ElsaMark class="dsb-ic dsb-ic--elsa" aria-hidden="true" />
        <span v-if="asking">ELSA is reading “{{ askingText }}”…</span>
        <span v-else-if="askError">{{ askError }}</span>
        <span v-else-if="asked && asked.understood.length">ELSA set these filters</span>
        <span v-else>ELSA found nothing to filter on in that. Try the normal search.</span>
        <button
          v-if="!asking"
          type="button"
          class="dsb-x"
          aria-label="Close"
          data-test="dsb-ask-close"
          @click="dismissAsk"
        >
          ×
        </button>
      </div>
      <template v-if="asked && !asking">
        <div v-if="asked.understood.length" class="dsb-chips">
          <span v-for="u in asked.understood" :key="u" class="dsb-chip">{{ u }}</span>
        </div>
        <p v-if="asked.not_understood.length" class="dsb-ask-note" data-test="dsb-ask-unknown">
          Not used: {{ asked.not_understood.join(', ') }}
        </p>
        <div v-if="asked.understood.length" class="dsb-ask-actions">
          <button type="button" class="dsb-link" data-test="dsb-ask-clear" @click="clearAsk">
            Clear these filters
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  BuildingOffice2Icon,
  ClockIcon,
  MagnifyingGlassIcon,
  UserIcon,
} from '@heroicons/vue/24/outline';
import ElsaMark from '@/components/Dashboard/Order/components/ElsaMark.vue';
import {
  SUGGEST_MIN_CHARS,
  askElsaSearch,
  looksLikeAQuestion,
  useSearchSuggest,
  type AskResult,
  type SuggestOrder,
} from '@/composables/useSearchSuggest';

type Kind = 'order' | 'company' | 'customer' | 'recent' | 'ask';
interface Option {
  index: number;
  key: string;
  kind: Kind;
  text: string;
  order?: SuggestOrder;
}
interface Group {
  title: string;
  kind: Kind;
  options: Option[];
}

const props = withDefaults(
  defineProps<{
    modelValue: string;
    delayMs?: number;
    /** ELSA may be asked (the same gate as Ask ELSA on the order). */
    canAsk?: boolean;
  }>(),
  { delayMs: undefined, canAsk: false }
);
const emit = defineEmits<{
  /** The text changed (the table's own search state). */
  (e: 'update:modelValue', value: string): void;
  /** Run the search for the current text. */
  (e: 'search'): void;
  /** Open this order. */
  (e: 'open', order: SuggestOrder): void;
  /** ELSA read a sentence: apply its tab and filters. */
  (e: 'asked', result: AskResult): void;
  /** Take ELSA's filters off again. */
  (e: 'clear-asked'): void;
}>();

const { suggestions, loading, recent, update, remember, forget, stop } = useSearchSuggest(
  props.delayMs
);

const root = ref<HTMLElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const focused = ref(false);
const dismissed = ref(false);
const active = ref(-1);

const typed = computed(() => (props.modelValue ?? '').trim());
const longEnough = computed(() => typed.value.replace(/[#\s]/g, '').length >= SUGGEST_MIN_CHARS);

const groups = computed<Group[]>(() => {
  const out: Group[] = [];
  let index = 0;
  const add = (title: string, kind: Kind, options: Omit<Option, 'index'>[]): void => {
    if (options.length)
      out.push({ title, kind, options: options.map((o) => ({ ...o, index: index++ })) });
  };
  if (!longEnough.value) {
    add(
      'Recent searches',
      'recent',
      recent.value.map((text) => ({ key: `r-${text}`, kind: 'recent' as const, text }))
    );
    return out;
  }
  // An answer for an older text is not shown against the new one.
  if (suggestions.value.q !== typed.value) return out;
  add(
    'Orders',
    'order',
    suggestions.value.orders.map((order) => ({
      key: `o-${order.order_item_id}`,
      kind: 'order' as const,
      text: String(order.order_no ?? ''),
      order,
    }))
  );
  add(
    'Companies',
    'company',
    suggestions.value.companies.map((text) => ({
      key: `c-${text}`,
      kind: 'company' as const,
      text,
    }))
  );
  add(
    'Customers',
    'customer',
    suggestions.value.customers.map((text) => ({
      key: `u-${text}`,
      kind: 'customer' as const,
      text,
    }))
  );
  return out;
});
const listed = computed(() => groups.value.flatMap((g) => g.options));
// A sentence, and ELSA allowed: one more option at the end of the list.
const askOption = computed<Option | null>(() =>
  props.canAsk && looksLikeAQuestion(typed.value)
    ? { index: listed.value.length, key: 'ask', kind: 'ask', text: typed.value }
    : null
);
const options = computed(() =>
  askOption.value ? [...listed.value, askOption.value] : listed.value
);

const open = computed(() => {
  if (!focused.value || dismissed.value || asking.value || asked.value || askError.value)
    return false;
  if (!longEnough.value) return groups.value.length > 0;
  return true;
});

const statusText = (status: string): string => {
  const s = status.replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
};
const iconFor = (kind: Kind) =>
  kind === 'company' ? BuildingOffice2Icon : kind === 'customer' ? UserIcon : ClockIcon;

const onInput = (value: string): void => {
  if (!asking.value) dismissAsk();
  dismissed.value = false;
  active.value = -1;
  emit('update:modelValue', value);
  update(value);
};

const move = (step: number): void => {
  if (!open.value) {
    dismissed.value = false;
    return;
  }
  const n = options.value.length;
  if (!n) return;
  active.value = active.value < 0 ? (step > 0 ? 0 : n - 1) : (active.value + step + n) % n;
  void nextTick(() =>
    root.value?.querySelector(`#dsb-opt-${active.value}`)?.scrollIntoView?.({ block: 'nearest' })
  );
};

const close = (): void => {
  dismissed.value = true;
  active.value = -1;
};

/** Search for a text: put it in the field, remember it, run the table's search. */
const runSearch = (text: string): void => {
  stop();
  close();
  if (text !== props.modelValue) emit('update:modelValue', text);
  remember(text);
  void nextTick(() => emit('search'));
};

/* ---- ask ELSA ---- */
const asking = ref(false);
const askingText = ref('');
const asked = ref<AskResult | null>(null);
const askError = ref('');

const ask = async (text: string): Promise<void> => {
  if (asking.value) return;
  stop();
  close();
  asking.value = true;
  askingText.value = text;
  asked.value = null;
  askError.value = '';
  try {
    const result = await askElsaSearch(text);
    asked.value = result;
    if (!result.understood.length) return;
    remember(text);
    emit('asked', result);
    // The words ELSA left as text become the search; the sentence itself does not.
    emit('update:modelValue', result.search);
    void nextTick(() => emit('search'));
  } catch (e) {
    askError.value = (e as Error).message;
  } finally {
    asking.value = false;
  }
};
const dismissAsk = (): void => {
  asked.value = null;
  askError.value = '';
};
const clearAsk = (): void => {
  dismissAsk();
  emit('clear-asked');
  emit('update:modelValue', '');
  void nextTick(() => emit('search'));
};

const choose = (opt: Option): void => {
  if (opt.kind === 'ask') {
    void ask(opt.text);
    return;
  }
  if (opt.kind === 'order' && opt.order) {
    remember(typed.value);
    close();
    emit('open', opt.order);
    return;
  }
  runSearch(opt.text);
};

const onEnter = (): void => {
  const opt = open.value && active.value >= 0 ? options.value[active.value] : undefined;
  if (opt) choose(opt);
  else runSearch(props.modelValue ?? '');
};

// The highlight never points past the end of a new list.
watch(options, (list) => {
  if (active.value >= list.length) active.value = -1;
});

const onDocDown = (e: MouseEvent): void => {
  if (root.value && !root.value.contains(e.target as Node)) {
    focused.value = false;
    active.value = -1;
  }
};
onMounted(() => document.addEventListener('mousedown', onDocDown));
onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocDown);
  stop();
});
</script>

<style scoped>
.dsb {
  position: relative;
}
.dsb-pop {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 40;
  max-height: min(60vh, 440px);
  overflow-y: auto;
  padding: 6px;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: var(--shadow-2);
}
.dsb-grp {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 8px 4px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-mute);
}
.dsb-clear {
  font-size: 11px;
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0;
  color: var(--ink-soft);
  background: none;
  border: 0;
  cursor: pointer;
}
.dsb-clear:hover {
  color: var(--ink);
}
.dsb-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  min-height: 34px;
  padding: 6px 8px;
  text-align: left;
  font-size: 13px;
  color: var(--ink);
  background: none;
  border: 0;
  border-radius: 7px;
  cursor: pointer;
}
.dsb-opt--on {
  background: var(--surface-2);
}
.dsb-no {
  flex: none;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}
.dsb-main {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.dsb-sub {
  color: var(--ink-soft);
}
.dsb-status,
.dsb-hint {
  flex: none;
  font-size: 11px;
  color: var(--ink-mute);
}
.dsb-ic {
  flex: none;
  width: 16px;
  height: 16px;
  color: var(--ink-mute);
}
.dsb-note,
.dsb-foot {
  padding: 10px 8px;
  font-size: 12px;
  color: var(--ink-soft);
}
.dsb-foot {
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid var(--border-soft);
  color: var(--ink-mute);
}
.dsb-foot kbd {
  padding: 0 4px;
  margin: 0 1px;
  font: inherit;
  font-size: 11px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--surface-2);
}
.dsb-opt--ask {
  margin-top: 4px;
  border-top: 1px solid var(--border-soft);
  border-radius: 0 0 7px 7px;
  padding-top: 9px;
}
/* ELSA's accent is the agent purple, kept away from the brand teal. */
.dsb-ic--elsa {
  color: #7c5cd6;
}
.dsb-ask {
  padding: 10px 12px 12px;
}
.dsb-ask-head {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink);
}
.dsb-ask-head span {
  flex: 1;
  min-width: 0;
}
.dsb-x {
  flex: none;
  width: 24px;
  height: 24px;
  font-size: 16px;
  line-height: 1;
  color: var(--ink-soft);
  background: none;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
}
.dsb-x:hover {
  background: var(--surface-2);
  color: var(--ink);
}
.dsb-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}
.dsb-chip {
  padding: 3px 9px;
  font-size: 12px;
  color: var(--ink);
  background: var(--surface-2);
  border: 1px solid var(--border);
  border-radius: 999px;
}
.dsb-ask-note {
  margin: 8px 0 0;
  font-size: 12px;
  color: var(--ink-mute);
}
.dsb-ask-actions {
  margin-top: 10px;
}
.dsb-link {
  padding: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--accent);
  background: none;
  border: 0;
  cursor: pointer;
}
.dsb-link:hover {
  text-decoration: underline;
}
</style>
